/**
 * UI-Konstanten/Helfer für die Benachrichtigungs-Kadenz je Postfach (SP5).
 *
 * Single-Source der UI-Kadenz-Semantik (Labels, Intervall-Bedarf, Klammerung).
 * Die Caps sind exakte Spiegel der SP3-Backend-Grenzen
 * (`notification-preference.dto.ts`: MAX_INTERVAL_HOURS=24, MAX_INTERVAL_MINUTES=1440),
 * damit FE und BE nicht driften (Konsistenz R5).
 *
 * KEIN Import von Backend-Typen — `NotificationCadence` ist ein 1:1-FE-Mirror des
 * SP3-DTO-Contracts (separate Pakete @kmvs/api vs @kmvs/web).
 */

/** FE-Mirror von `NotificationCadence` (SP3 `notification-preference.dto.ts`). */
export type NotificationCadence =
  | "IMMEDIATE"
  | "EVERY_2H"
  | "EVERY_X_HOURS"
  | "EVERY_X_MINUTES";

/** Kadenz-Optionen mit deutschen Labels (Vorplan §5). */
export const CADENCE_OPTIONS: ReadonlyArray<{
  value: NotificationCadence;
  label: string;
}> = [
  { value: "IMMEDIATE", label: "Sofort" },
  { value: "EVERY_2H", label: "Alle 2 Stunden" },
  { value: "EVERY_X_HOURS", label: "Alle X Stunden" },
  { value: "EVERY_X_MINUTES", label: "Alle X Minuten" },
];

/** Obergrenzen für `intervalValue` — Spiegel der SP3-Caps. */
export const MAX_INTERVAL_HOURS = 24;
export const MAX_INTERVAL_MINUTES = 1440;

/** Sinnvolle Startwerte beim Wechsel auf eine Intervall-Kadenz (Mini-Entscheidung M6). */
export const DEFAULT_INTERVAL_HOURS = 3;
export const DEFAULT_INTERVAL_MINUTES = 30;

/** true, wenn die Kadenz einen Intervallwert benötigt (EVERY_X_HOURS/EVERY_X_MINUTES). */
export function isIntervalCadence(c: NotificationCadence): boolean {
  return c === "EVERY_X_HOURS" || c === "EVERY_X_MINUTES";
}

/** Maximalwert der jeweiligen Intervall-Kadenz (0 = kein Intervall). */
export function maxIntervalFor(c: NotificationCadence): number {
  if (c === "EVERY_X_HOURS") return MAX_INTERVAL_HOURS;
  if (c === "EVERY_X_MINUTES") return MAX_INTERVAL_MINUTES;
  return 0;
}

/**
 * Klemmt/validiert den Intervallwert kadenz-abhängig auf [1..max] und macht ihn
 * ganzzahlig. Spiegel der SP3-M3-Caps (Stunden ≤24, Minuten ≤1440, min 1).
 */
export function clampInterval(c: NotificationCadence, v: number): number {
  const max = maxIntervalFor(c);
  if (max === 0) return v; // keine Intervall-Kadenz → unverändert
  const int = Math.trunc(Number.isFinite(v) ? v : 1);
  if (int < 1) return 1;
  if (int > max) return max;
  return int;
}

/** Sinnvoller Default beim Wechsel auf eine Intervall-Kadenz (sonst 0). */
export function defaultIntervalFor(c: NotificationCadence): number {
  if (c === "EVERY_X_HOURS") return DEFAULT_INTERVAL_HOURS;
  if (c === "EVERY_X_MINUTES") return DEFAULT_INTERVAL_MINUTES;
  return 0;
}

/** Einheit für das Intervall-Label ("Stunden"/"Minuten"/""). */
export function intervalUnitLabel(
  c: NotificationCadence,
): "Stunden" | "Minuten" | "" {
  if (c === "EVERY_X_HOURS") return "Stunden";
  if (c === "EVERY_X_MINUTES") return "Minuten";
  return "";
}
